import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  ChevronDown,
  User,
  Settings,
  LogOut,
  ChevronRight,
  History,
  LayoutDashboard,
  Bell,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { env } from "../../../../config/env";

const ProfileUserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeRole, setActiveRole] = useState<"user" | "admin">("user");
  const menuRef = useRef<HTMLDivElement>(null);

  const hasRole = (roleName: string) => user?.roles?.some((r: any) => r.name === roleName) ?? false;
  const isAdmin = hasRole("admin") || hasRole("platform_admin");

  useEffect(() => {
    if (!isAdmin && activeRole === "admin") setActiveRole("user");
    if (isAdmin && activeRole !== "admin" && activeRole !== "user") setActiveRole("user");
  }, [isAdmin, activeRole]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayUser = useMemo(() => {
    return {
      name: user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || "Utilisateur" : "Utilisateur",
      email: user?.email ?? "",
      avatar: "/assets/images/defaultuser.png", // toujours l'image par défaut
    };
  }, [user]);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate("/auth/login");
  };

  const userItems = [
    { icon: <User size={16} />, label: "Tableau de bord", path: "/user/dashboard/home" },
    { icon: <History size={16} />, label: "Historique", path: "/user/dashboard/payment-history" },
    { icon: <Settings size={16} />, label: "Paramètres", path: "/user/dashboard/settings" },
  ];

  const adminItems = [
    { icon: <LayoutDashboard size={16} />, label: "Dashboard admin", path: "/admin/dashboard/home" },
    { icon: <Settings size={16} />, label: "Paramètres admin", path: "/admin/dashboard/settings" },
    { icon: <Bell size={16} />, label: "Notifications", path: "/admin/dashboard/notifications" },
  ];

  const currentItems = activeRole === "admin" ? adminItems : userItems;

  // Si utilisateur non connecté : le clic redirige vers login (pas de menu)
  const handleTriggerClick = () => {
    if (!user) {
      navigate("/auth/login");
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="relative pt-0.5" ref={menuRef}>
      <motion.button
        onClick={handleTriggerClick}
        className="relative bg-white rounded-xl transition-all duration-300 px-2 py-[5px]"
        whileTap={{ scale: 0.97 }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-xl" />
        <div className="relative z-10 flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full overflow-hidden bg-white flex items-center justify-center">
            <img
              src={displayUser.avatar}
              alt={displayUser.name}
              className="w-full h-full object-cover"
            />
          </div>
          {/* Afficher chevron seulement si connecté (sinon, pas de rotation inutile) */}
          {user && (
            <ChevronDown
              size={18}
              className={`text-black transition-transform duration-200 font-black ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
      </motion.button>

      <AnimatePresence>
        {user && isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 30, duration: 0.2 }}
            className="absolute right-0 mt-2 w-64 rounded-2xl bg-white shadow-lg overflow-hidden z-50"
          >
            <div className="relative bg-gradient-to-br from-primary to-secondary p-4">
              <div className="relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/30 bg-white flex-shrink-0">
                    <img
                      src={displayUser.avatar}
                      alt={displayUser.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-white font-semibold text-sm truncate">{displayUser.name}</p>
                    <p className="text-xs text-white truncate">{displayUser.email}</p>
                  </div>
                </div>
                {isAdmin && (
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => setActiveRole("user")}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition ${
                        activeRole === "user"
                          ? "bg-white text-black shadow"
                          : "bg-secondary text-white hover:bg-white/30"
                      }`}
                    >
                      Utilisateur
                    </button>
                    <button
                      onClick={() => setActiveRole("admin")}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition ${
                        activeRole === "admin"
                          ? "bg-white text-black shadow"
                          : "bg-secondary text-white hover:bg-white/30"
                      }`}
                    >
                      Administrateur
                    </button>
                  </div>
                )}
                <hr className="w-full border-white/20 mt-3" />
              </div>
            </div>
            <div className="py-2">
              {currentItems.map((item, idx) => (
                <MenuItem
                  key={idx}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => {
                    setIsOpen(false);
                    navigate(item.path);
                  }}
                />
              ))}
              <div className="my-1 border-t border-gray-100" />
              <MenuItem
                icon={<LogOut size={16} />}
                label="Déconnexion"
                onClick={handleLogout}
                isDanger
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// MenuItem (inchangé)
interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isDanger?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, onClick, isDanger }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all ${
      isDanger ? "text-red-500 hover:bg-red-50" : "text-gray-700 hover:bg-primary/5"
    }`}
  >
    <div className="flex items-center gap-3">
      {icon}
      <span>{label}</span>
    </div>
    <ChevronRight size={14} className="text-gray-400" />
  </button>
);

export default ProfileUserMenu;